// client/src/api/archive.js
// API client for Archive functionality
import api from '../utils/api';

/**
 * Upload image and perform OCR extraction
 * @param {File} imageFile - Image file to upload
 * @returns {Promise<Object>} { imageUrl, s3Key, fields, ocrRaw }
 */
export const uploadAndExtract = async (imageFile) => {
  const formData = new FormData();
  formData.append('image', imageFile);

  const response = await api.post('/api/archive/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Enhanced scan endpoint:
 * - OCR + field mapping + per-field confidence
 * - Theme colour extraction with guaranteed contrast
 *
 * @param {Object} params
 * @param {File|Blob} params.imageFile - OCR-optimized image (required)
 * @param {File|Blob} [params.colorImageFile] - Colour-sampling image (optional)
 * @param {boolean} [params.saveImage] - If true, backend may store in S3 (optional)
 */
export const scanBusinessCard = async ({ imageFile, colorImageFile, saveImage = true }) => {
  const formData = new FormData();
  formData.append('image', imageFile);
  if (colorImageFile) formData.append('colorImage', colorImageFile);
  formData.append('saveImage', String(!!saveImage));

  const response = await api.post('/api/archive/scan-business-card', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Lookup existing archived card by email (dedupe UX).
 * @param {string} email
 * @returns {Promise<{card: { _id: string, fields: Object, theme: Object|null, primaryColor: string|null, imageUrl: string|null }}>}
 */
export const getArchivedCardByEmail = async (email) => {
  const params = new URLSearchParams();
  params.append('email', email);
  const response = await api.get(`/api/archive/by-email?${params.toString()}`);
  return response.data;
};

/**
 * Create a new archived card
 * @param {Object} cardData - { imageUrl, fields, s3Key?, ocrRaw? }
 * @returns {Promise<Object>} Created card
 */
export const createArchivedCard = async (cardData) => {
  const response = await api.post('/api/archive', cardData);
  return response.data;
};

/**
 * Get all archived cards (paginated)
 * @param {Object} params - { page?, limit? }
 * @returns {Promise<Object>} { cards, pagination }
 */
export const getArchivedCards = async (params = {}) => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);

  const url = `/api/archive${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await api.get(url);
  return response.data;
};

/**
 * Update an archived card
 * @param {string} cardId - Card ID
 * @param {Object} updates - { fields }
 * @returns {Promise<Object>} Updated card
 */
export const updateArchivedCard = async (cardId, updates) => {
  const response = await api.patch(`/api/archive/${cardId}`, updates);
  return response.data;
};

/**
 * Delete an archived card
 * @param {string} cardId - Card ID
 * @returns {Promise<void>}
 */
export const deleteArchivedCard = async (cardId) => {
  await api.delete(`/api/archive/${cardId}`);
};

/**
 * Send invite email to archived card owner
 * @param {string} cardId - Card ID
 * @returns {Promise<Object>} { message, invitedAt, inviteCount }
 */
export const inviteToVelox = async (cardId) => {
  const response = await api.post(`/api/archive/${cardId}/invite`);
  return response.data;
};

